import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _lowerCase from "lodash/lowerCase";
import _pick from "lodash/pick";
import _isEmpty from "lodash/isEmpty";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.regexp.to-string.js";
import "core-js/modules/es6.regexp.search.js";
import url from 'url';
import querystring from 'querystring';
import analytics from '@nerdwallet/analytics';
import redact from '@nerdwallet/nw-client-lib/redact';
import { parseGraphQLErrorDetails } from "../../actions/utils";
/**
 * Emits the toast_message_impression analytic event
 * @param {*} trackingState
 * @param {*} payload
 */

export var fireToasterEvent = function fireToasterEvent(trackingState, payload) {
  var entityName = "".concat(trackingState.appName, "_toast").toLowerCase();
  analytics.track('toast_message_impression', {
    entity_name: entityName,
    additional_attributes: {
      toast_type: payload.title || payload.message,
      // there are no priorities set among the different messages, so defaulting
      priority: 0,
      severity: payload.type === 'error' ? 'error' : 'info'
    }
  });
};
/**
 * Parse out the contents of a response to extract key error details.
 * Copied from mobile-creditcards repository: https://github.com/NerdWallet/mobile-creditcards/blob/develop/App/Lib/network-utils.ts
 * @param {*} resp
 * @returns
 */

export var parseErrorDetails = function parseErrorDetails(resp) {
  var _innerResp$rawRequest, _innerResp$rawRequest2, _serverError$data;

  if (!_isEmpty(resp.graphQLErrors) && !resp.networkError) {
    // parse through the graphQL response for error details
    return parseGraphQLErrorDetails(resp.graphQLErrors[0]);
  }

  if (resp instanceof Error) {
    var _resp$networkError, _resp$networkError$re, _resp$networkError2, _resp$userInfo;

    var code = resp === null || resp === void 0 ? void 0 : resp.code;
    var msg = resp === null || resp === void 0 ? void 0 : resp.message;

    var _requestUrl = resp === null || resp === void 0 ? void 0 : (_resp$networkError = resp.networkError) === null || _resp$networkError === void 0 ? void 0 : (_resp$networkError$re = _resp$networkError.response) === null || _resp$networkError$re === void 0 ? void 0 : _resp$networkError$re.url;

    return {
      httpStatusCode: resp === null || resp === void 0 ? void 0 : (_resp$networkError2 = resp.networkError) === null || _resp$networkError2 === void 0 ? void 0 : _resp$networkError2.statusCode,
      systemErrorCode: code,
      systemErrorMessage: msg,
      errorType: resp === null || resp === void 0 ? void 0 : (_resp$userInfo = resp.userInfo) === null || _resp$userInfo === void 0 ? void 0 : _resp$userInfo.ErrorType,
      responseText: msg !== null && msg !== void 0 ? msg : resp.toString(),
      url: _requestUrl,
      requestUrl: _requestUrl
    };
  }

  var data = resp.data,
      systemError = resp.err,
      innerResp = resp.resp;

  if (!(innerResp instanceof Object)) {
    return {};
  }

  var requestUrl = innerResp.url,
      httpStatusCode = innerResp.statusCode,
      httpMethod = innerResp.method;
  var serverError = data === null || data === void 0 ? void 0 : data.error; // Parse the "response text" from the rawRequest object if it is of a serializable string type
  // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType
  // eslint-disable-next-line no-underscore-dangle

  var responseType = innerResp === null || innerResp === void 0 ? void 0 : (_innerResp$rawRequest = innerResp.rawRequest) === null || _innerResp$rawRequest === void 0 ? void 0 : _innerResp$rawRequest._responseType; // eslint-disable-next-line no-underscore-dangle

  var response = innerResp === null || innerResp === void 0 ? void 0 : (_innerResp$rawRequest2 = innerResp.rawRequest) === null || _innerResp$rawRequest2 === void 0 ? void 0 : _innerResp$rawRequest2._response;
  var responseText;

  if (responseType === 'json') {
    responseText = JSON.stringify(response);
  } else if (responseType === '' || responseType === 'text') {
    responseText = response;
  }

  return {
    url: requestUrl,
    requestUrl: requestUrl,
    httpMethod: httpMethod,
    httpStatusCode: httpStatusCode,
    systemErrorCode: systemError === null || systemError === void 0 ? void 0 : systemError.code,
    systemErrorMessage: systemError === null || systemError === void 0 ? void 0 : systemError.message,
    internalMessage: serverError === null || serverError === void 0 ? void 0 : (_serverError$data = serverError.data) === null || _serverError$data === void 0 ? void 0 : _serverError$data.internal_message,
    userMessage: serverError === null || serverError === void 0 ? void 0 : serverError.data.user_message,
    errorType: serverError === null || serverError === void 0 ? void 0 : serverError.type,
    responseText: responseText
  };
};
/**
 * @param {String} originalUrl - the URL to which query string will be redacted
 * @param {Array} additionalKeys - optional list of additional keys to redact
 * @returns {String} A new URL string with the query string param values been redacted.
 */

var redactUrl = function redactUrl(originalUrl) {
  var additionalKeys = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!originalUrl) return originalUrl;
  var parsedUrl = url.parse(originalUrl);
  if (!parsedUrl.query) return originalUrl;
  parsedUrl.search = querystring.stringify(redact(querystring.parse(parsedUrl.query), additionalKeys));
  return url.format(parsedUrl);
};
/**
 * Emits a network_request_error_occurred event for failed request actions
 * @param {*} action
 */


export var fireRequestErrorEvent = function fireRequestErrorEvent(action) {
  var _action$meta;

  var response = action === null || action === void 0 ? void 0 : (_action$meta = action.meta) === null || _action$meta === void 0 ? void 0 : _action$meta.resp;

  if (response) {
    var _eventProps$systemErr;

    var errorDetails = parseErrorDetails(response);
    var isRecoverable = errorDetails.systemErrorCode === 'ETIMEDOUT' || // Request failed due to poor connectivity
    errorDetails.httpStatusCode === 0 || // Device was offline when the request was made
    errorDetails.httpStatusCode >= 500 && errorDetails.httpStatusCode < 600; // Request failed due to potentially-ephemeral server error

    var eventProps = _objectSpread(_objectSpread({
      isRecoverable: isRecoverable
    }, errorDetails), _pick(action.meta, ['actionName', 'requestDuration', 'requestStartTime', 'retries', 'retryId'])); // redact url before tracking the error.


    eventProps.url = redactUrl(eventProps.url);
    analytics.track('network_request_error_occurred', {
      error_code: eventProps.systemErrorCode,
      error_name: "".concat(action.meta.actionName, "_fail"),
      error_message: (_eventProps$systemErr = eventProps.systemErrorMessage) !== null && _eventProps$systemErr !== void 0 ? _eventProps$systemErr : eventProps.internalMessage,
      additional_attributes: {
        url: eventProps.url,
        http_method: _lowerCase(eventProps.httpMethod),
        http_status_code: eventProps.httpStatusCode,
        user_message: eventProps.userMessage,
        response_text: eventProps.responseText,
        request_duration: eventProps.requestDuration,
        request_start_time: eventProps.requestStartTime,
        is_recoverable: isRecoverable
      }
    });
  }
};